// Ảnh đại diện của đơn vị (30/10/2026): vòng ảnh dùng chung (ô tên góc trái điện thoại, Cài đặt, cột menu máy tính) +
// chọn / xóa ảnh. Ảnh được cắt vuông giữa, thu về AVATAR_RULES.side px và đổi sang JPEG ngay trên máy rồi mới gửi
// PUT /settings/shop/avatar — máy chủ chỉ kiểm lại (packages/common/src/avatar.ts). Chưa có ảnh → chữ cái đầu tên.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Camera } from 'lucide-react'
import { AVATAR_RULES } from '@shoptrack/common/avatar'
import { del, put } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getAuthState } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { initialOf, type ShopSettings } from '@/components/layout/shell-model'

/** Vòng ảnh đại diện; chưa có ảnh / ảnh lỗi → chữ cái đầu tên. Cỡ, màu nền chữ do `className` quyết định. */
export function UnitAvatar({ name, src, className, children }: { name: string; src?: string | null; className?: string; children?: ReactNode }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src])
  return (
    <span className={cn('relative shrink-0 overflow-visible', className)}>
      {src && !failed ? (
        <img src={src} alt="" onError={() => setFailed(true)} className="size-full rounded-full object-cover" />
      ) : (
        initialOf(name || 'S')
      )}
      {children}
    </span>
  )
}

async function loadImage(file: File): Promise<{ img: CanvasImageSource; w: number; h: number }> {
  try {
    // Ảnh chụp điện thoại: xoay theo EXIF
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' })
    return { img: bmp, w: bmp.width, h: bmp.height }
  } catch {
    const url = URL.createObjectURL(file)
    try {
      const img = new Image()
      img.src = url
      await img.decode()
      return { img, w: img.naturalWidth, h: img.naturalHeight }
    } finally {
      URL.revokeObjectURL(url)
    }
  }
}

/** Cắt vuông giữa ảnh, thu về AVATAR_RULES.side px, JPEG (giảm chất lượng tới khi vừa dung lượng). Trả data URL. */
export async function shrinkAvatar(file: File): Promise<string> {
  const { img, w, h } = await loadImage(file)
  const side = Math.min(w, h)
  if (!side) throw new Error('Không đọc được ảnh này')
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = AVATAR_RULES.side
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Trình duyệt không xử lý được ảnh')
  ctx.fillStyle = '#ffffff' // ảnh PNG trong suốt → nền trắng
  ctx.fillRect(0, 0, AVATAR_RULES.side, AVATAR_RULES.side)
  ctx.drawImage(img, (w - side) / 2, (h - side) / 2, side, side, 0, 0, AVATAR_RULES.side, AVATAR_RULES.side)
  for (const q of [0.86, 0.72, 0.58]) {
    const url = canvas.toDataURL('image/jpeg', q)
    const bytes = Math.floor(((url.length - url.indexOf(',') - 1) * 3) / 4)
    if (bytes <= AVATAR_RULES.maxBytes) return url
  }
  throw new Error('Ảnh quá chi tiết, chọn ảnh khác')
}

/** Chọn / xóa ảnh đại diện của đơn vị đang mở. `input` phải được vẽ ra (ô chọn file ẩn). */
export function useAvatarEditor() {
  const qc = useQueryClient()
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const shopId = getAuthState().activeShopId
  const setUrl = (avatarUrl: string | null) =>
    qc.setQueryData<ShopSettings | null>(['shop-settings', shopId], (old) => (old ? { ...old, avatarUrl } : old))

  async function onFile(file: File) {
    if (!file.type.startsWith('image/')) return toast.error('Chọn một file ảnh')
    if (file.size > AVATAR_RULES.maxSourceBytes) return toast.error(`Ảnh quá ${AVATAR_RULES.maxSourceBytes / 1024 / 1024} MB`)
    setBusy(true)
    try {
      const data = await shrinkAvatar(file)
      const res = await put<{ avatarUrl: string }>('/settings/shop/avatar', { data })
      setUrl(res.avatarUrl)
      toast.success('Đã đổi ảnh đại diện')
    } catch (e) {
      toast.error((e as Error)?.message || 'Không đổi được ảnh')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      await del('/settings/shop/avatar')
      setUrl(null)
      toast.success('Đã xóa ảnh đại diện')
    } catch (e) {
      toast.error((e as Error)?.message || 'Không xóa được ảnh')
    } finally {
      setBusy(false)
    }
  }

  const input = (
    <input
      ref={ref}
      type="file"
      accept="image/*"
      hidden
      onChange={(e) => {
        const f = e.target.files?.[0]
        e.target.value = '' // chọn lại đúng file đó vẫn chạy
        if (f) void onFile(f)
      }}
    />
  )
  return { pick: () => ref.current?.click(), remove, busy, input }
}

/** Ô "Ảnh đại diện" của Cài đặt › Thông tin chung: ảnh + Đổi ảnh / Xóa ảnh. */
export function AvatarField({ name, src }: { name: string; src: string | null | undefined }) {
  const { pick, remove, busy, input } = useAvatarEditor()
  return (
    <section className="flex flex-wrap items-center gap-4 rounded-xl bg-raise p-6 shadow-card">
      {input}
      <UnitAvatar name={name} src={src} className="grid size-16 place-items-center rounded-full bg-accent text-xl font-semibold text-onAccent" />
      <div className="flex min-w-0 flex-col gap-2">
        <h3 className="text-base font-medium text-ink">Ảnh đại diện</h3>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={pick} disabled={busy}>
            <Camera className="size-4" aria-hidden />
            {src ? 'Đổi ảnh' : 'Chọn ảnh'}
          </Button>
          {src && (
            <Button type="button" size="sm" variant="ghost" onClick={() => void remove()} disabled={busy}>
              Xóa ảnh
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}
